import { paths } from '@common/types';
import { axiosInstance } from '@config/axios/axios.config';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

const createPartnerRequest = async (
  props: paths['/partner']['post']['requestBody']['content']['application/json'],
) => {
  const response = axiosInstance.post<
    paths['/partner']['post']['requestBody']['content']['application/json'],
    AxiosResponse<
      paths['/partner']['post']['responses']['201']['content']['application/json']
    >
  >(`/partner`, {
    ...props,
  });
  const data = await response;

  return data;
};

export const useCreatePartnerMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['partner'],
    mutationFn: (
      props: paths['/partner']['post']['requestBody']['content']['application/json'],
    ) => createPartnerRequest(props),
    onSuccess: () => client.invalidateQueries({ queryKey: ['partners'] }),
  });

  return mutation;
};

const editPartnerRequest = async (
  props: paths['/partner/{record_id}']['patch']['requestBody']['content']['application/json'] & {
    partner_id: string;
  },
) => {
  const response = axiosInstance.patch<
    paths['/partner/{record_id}']['patch']['requestBody']['content']['application/json']
  >(`/partner/${props.partner_id}`, {
    ...props,
  });
  const data = await response;

  return data;
};

export const useEditPartnerMutation = (partner_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['editPartner'],
    mutationFn: (
      props: paths['/partner/{record_id}']['patch']['requestBody']['content']['application/json'] & {
        partner_id: string;
      },
    ) => editPartnerRequest(props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['partners'] }),
        client.invalidateQueries({
          queryKey: ['partner', partner_id],
        }),
        client.invalidateQueries({
          queryKey: ['partnerHistory', partner_id],
        }),
      ]),
  });

  return mutation;
};

const uploadPartnerLogoRequest = async (props: {
  id: string;
  file: FormData;
}) => {
  const response = axiosInstance.put<{
    id: string;
    file: FormData;
  }>(`/partner/${props.id}/logo`, props.file, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  const data = await response;

  return data;
};

export const useUploadPartnerLogoMutation = (partner_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['partnerLogoUpload'],
    mutationFn: (props: { id: string; file: FormData }) =>
      uploadPartnerLogoRequest(props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['partners'] }),
        client.invalidateQueries({
          queryKey: ['partner', partner_id, 'logo'],
        }),
      ]),
  });

  return mutation;
};

const deletePartnerLogoRequest = async (props: { id: string }) => {
  const response = axiosInstance.delete<{
    id: string;
  }>(`/partner/${props.id}/logo`);
  const data = await response;

  return data;
};

export const useDeletePartnerLogoMutation = (partner_id: string) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['deletePartnerLogo'],
    mutationFn: (props: { id: string }) => deletePartnerLogoRequest(props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['partners'] }),
        client.invalidateQueries({
          queryKey: ['partner', partner_id, 'logo'],
        }),
      ]),
  });

  return mutation;
};

export const partnersCsvDownloadRequest = async (
  props: paths['/record/partners/csv']['get']['parameters']['query'],
) => {
  const response = await axiosInstance.get(`/record/partners/csv`, {
    headers: {
      'Content-type': 'application/xml',
    },
    responseType: 'blob',
    params: props,
  });

  const data = (await response).data;

  return data;
};

export const usePartnersCsvDownloadQuery = () => {
  const mutation = useMutation({
    mutationKey: ['partnersCSVDownload'],
    mutationFn: (
      props: paths['/record/partners/csv']['get']['parameters']['query'],
    ) => partnersCsvDownloadRequest(props),
  });

  return mutation;
};

export const partnerTemplateImportRequest = async (
  record_id: string,
  program_id: string,
  file: FormData,
) => {
  const response = await axiosInstance.put(
    `/partner/${record_id}/welcome-letter-template/${program_id}`,
    file,
    {
      headers: {
        'Content-type': '.docx',
      },
    },
  );

  const data = (await response).data;

  return data;
};

export const usePartnerTemplateImportMutation = () => {
  const mutation = useMutation({
    mutationKey: ['partnerTemplateImport'],
    mutationFn: ({
      file,
      record_id,
      program_id,
    }: {
      file: FormData;
      record_id: string;
      program_id: string;
    }) => partnerTemplateImportRequest(record_id, program_id, file),
  });

  return mutation;
};
